import { useEffect } from 'react';
import type { Move } from '../types';

interface MoveDetailSheetProps {
  move: Move | null;
  isOpen: boolean;
  onClose: () => void;
  onStatusChange: (id: number, status: 'done' | 'skipped' | 'active') => void;
  onCompose?: (move: Move) => void;
}

const MOVE_TYPE_LABELS: Record<string, string> = {
  follow_up: 'Follow up',
  send_document: 'Send Document',
  schedule: 'Schedule',
  call: 'Call',
  other: 'Task',
};

const URGENCY_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  overdue: { bg: 'bg-red-500/20', text: 'text-red-500', label: 'Overdue' },
  time_sensitive: { bg: 'bg-amber-500/20', text: 'text-amber-500', label: 'Soon' },
};

function formatDueDate(iso: string): string {
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function computeFollowUpDate(createdAt: string, days: number): string {
  const d = new Date(createdAt);
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function MoveDetailSheet({ move, isOpen, onClose, onStatusChange, onCompose }: MoveDetailSheetProps) {
  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen || !move) return null;

  const isCompleted = move.status === 'done' || move.status === 'skipped';
  const isOutgoing = move.direction === 'i_promised';
  const urgencyBadge = move.urgency && URGENCY_BADGE[move.urgency];
  const typeLabel = move.move_type ? (MOVE_TYPE_LABELS[move.move_type] ?? 'Task') : 'Task';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40 animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={move.label}
        className="fixed bottom-0 left-0 right-0 z-50 bg-canvas rounded-t-2xl shadow-xl max-h-[85vh] overflow-y-auto animate-slide-up md:max-w-2xl md:mx-auto"
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-ink-tertiary rounded-full" />
        </div>

        {/* Title */}
        <div className="px-6 pb-4">
          <h2 className="text-lg font-semibold text-ink leading-snug">{move.label}</h2>
        </div>

        {/* Contact + chips row */}
        <div className="px-6 pb-3 flex flex-wrap items-center gap-2">
          {move.contact_name && (
            <div className="flex items-center gap-1.5 bg-canvas-secondary rounded-full px-3 py-1">
              <span className="material-icons-round text-ink-tertiary" style={{ fontSize: '14px' }}>person</span>
              <span className="text-sm text-ink font-medium">{move.contact_name}</span>
            </div>
          )}
          {/* Move type chip */}
          <div className="flex items-center gap-1 bg-canvas-secondary rounded-full px-3 py-1">
            <span className="text-xs text-ink-secondary font-medium">{typeLabel}</span>
          </div>
        </div>

        {/* Direction indicator */}
        <div className="px-6 pb-3 flex items-center gap-2">
          <span
            className={`material-icons-round ${isOutgoing ? 'text-primary' : 'text-ink-secondary'}`}
            style={{ fontSize: '18px' }}
          >
            {isOutgoing ? 'arrow_upward' : 'arrow_downward'}
          </span>
          <span className="text-sm text-ink-secondary" data-ph-no-mask>
            {isOutgoing ? 'You promised' : 'They promised'}
          </span>
        </div>

        {/* Urgency badge */}
        {urgencyBadge && (
          <div className="px-6 pb-3">
            <span className={`inline-block text-xs font-bold px-2 py-1 rounded ${urgencyBadge.bg} ${urgencyBadge.text}`} data-ph-no-mask>
              {urgencyBadge.label}
            </span>
          </div>
        )}

        {/* Due date or follow-up window */}
        {move.due_date ? (
          <div className="px-6 pb-3 flex items-center gap-2">
            <span className="material-icons-round text-ink-tertiary" style={{ fontSize: '16px' }}>event</span>
            <span className="text-sm text-ink-secondary" data-ph-no-mask>Due {formatDueDate(move.due_date)}</span>
          </div>
        ) : move.follow_up_within_days ? (
          <div className="px-6 pb-3 flex items-center gap-2">
            <span className="material-icons-round text-ink-tertiary" style={{ fontSize: '16px' }}>schedule</span>
            <span className="text-sm text-ink-secondary" data-ph-no-mask>
              Follow up by {computeFollowUpDate(move.created_at, move.follow_up_within_days)}
            </span>
          </div>
        ) : null}

        {/* Source quote */}
        {move.source_quote && (
          <div className="px-6 pb-4">
            <div className="bg-canvas-secondary border-l-4 border-primary/40 rounded-r-lg px-4 py-3">
              <p className="text-sm text-ink-secondary italic leading-relaxed">"{move.source_quote}"</p>
            </div>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1 min-h-[1rem]" />

        {/* Bottom action bar */}
        <div className="px-6 pb-8 pt-4 border-t border-edge/50 flex gap-3">
          {onCompose && !isCompleted && (
            <button
              onClick={() => onCompose(move)}
              className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent/10 text-accent shrink-0"
              title="Send message"
            >
              <span className="material-icons-round" style={{ fontSize: '20px' }}>send</span>
            </button>
          )}
          {isCompleted ? (
            /* Undo button */
            <button
              onClick={() => onStatusChange(move.id, 'active')}
              className="flex-1 flex items-center justify-center gap-2 bg-canvas-secondary text-ink font-semibold py-3 rounded-xl transition-colors hover:bg-edge active:scale-95"
              data-ph-no-mask
            >
              <span className="material-icons-round" style={{ fontSize: '18px' }}>undo</span>
              Undo
            </button>
          ) : (
            <>
              {/* Skip button */}
              <button
                onClick={() => onStatusChange(move.id, 'skipped')}
                className="flex-1 flex items-center justify-center gap-2 bg-canvas-secondary text-ink-tertiary font-semibold py-3 rounded-xl transition-colors hover:bg-edge active:scale-95"
                data-ph-no-mask
              >
                <span className="material-icons-round" style={{ fontSize: '18px' }}>visibility_off</span>
                Skip
              </button>

              {/* Done button */}
              <button
                onClick={() => onStatusChange(move.id, 'done')}
                className="flex-1 flex items-center justify-center gap-2 bg-primary text-white font-semibold py-3 rounded-xl transition-colors hover:bg-primary active:scale-95"
                data-ph-no-mask
              >
                <span className="material-icons-round" style={{ fontSize: '18px' }}>check</span>
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default MoveDetailSheet;
